import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronRight, Plus, Trash2, AlertTriangle } from "lucide-react";
import withholdingTaxAPI from "../../api/withholdingTaxAPI";
import ChartOfAccountsSearch from "../ChartOfAccountsSearch";
import Button from "../Button";
import MoneyInput from "../MoneyInput";
import RestoreDraftModal from "../RestoreDraftModal";
import SaveOutcomePanel from "../SaveOutcomePanel";
import DraftStatusIndicator from "../DraftStatusIndicator";
import { AtcPicker, PayeePicker } from "./WhtPickers";
import MicroTaxpayerBanner from "./MicroTaxpayerBanner";
import notify from "../../lib/notify";
import useGuardedMutation from "../../hooks/useGuardedMutation";
import useEncodingDraft from "../../hooks/useEncodingDraft";
import { DRAFT_SCREENS } from "../../utils/encodingDraft";
import { setPendingIdempotencyKey } from "../../utils/idempotencyKey";
import { classifyRows, newClientRowId, rowsAfterSave, withClientRowIds } from "../../utils/encodingGrid";
import {
  computeTaxWithheldCentavos,
  formatCentavos,
  pesosToCentavos,
  percentToBasisPoints,
} from "../../utils/whtMoney";

// EWT encoding grid (docs/WHT_IMPLEMENTATION.md §4, F3). Mirrors VATOutputForm:
// 5 blank rows, add rows, browser-local draft autosave, incomplete rows stay
// highlighted, one all-or-nothing batch per save (F2 records API). The server
// computes the tax withheld; the grid shows the same half-up figure live.

export const MIN_ROWS = 5;

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export const buildEmptyRow = (month) => ({
  id: newClientRowId(),
  month: month || "",
  description: "",
  coaAccount: null,
  coaName: "",
  payee: null,          // { _id, name, unassigned, profile }
  atc: null,            // picked ATC option { code, rateBasisPoints, description }
  atcPayeeType: null,   // payee type the ATC was picked for (WI vs WC)
  lastAtcCode: null,    // last picked code, kept while the field is being edited
  atcCode: "",
  atcRateBasisPoints: null,
  rate: "",             // percent as typed, e.g. "5" or "0.5"
  base: 0,              // pesos (MoneyInput)
  invoiceNo: "",
  withholdingOnly: false,
  creditAccount: "cash",
});

const buildEmptyRows = (month) => Array.from({ length: MIN_ROWS }, () => buildEmptyRow(month));

// Module scope: the draft hook and the save path must agree on "untouched".
export const isRowEmpty = (row) =>
  !(row?.description || "").trim() && !Number(row?.base) && !row?.payee && !row?.atcCode && !(row?.invoiceNo || "").trim();

const rowBasisPoints = (row) => percentToBasisPoints(row.rate);
const rowBaseCentavos = (row) => pesosToCentavos(row.base);

const isRowIncomplete = (row) => {
  if (isRowEmpty(row)) return false;
  const bp = rowBasisPoints(row);
  return (
    !row.month ||
    !row.payee ||
    !row.atcCode ||
    !(rowBaseCentavos(row) > 0) ||
    !Number.isInteger(bp) || bp < 0 || bp > 10000
  );
};

const rowTaxCentavos = (row) => computeTaxWithheldCentavos(rowBaseCentavos(row), rowBasisPoints(row));

// Which grid cell shows a server field error. Anything unmapped shows under
// the description cell.
const FIELD_CELL = {
  month: "month",
  description: "description",
  coaAccount: "description",
  counterparty: "payee",
  atcCode: "atc",
  rateBasisPoints: "rate",
  baseCentavos: "base",
  invoiceNo: "detail",
  glMode: "detail",
  creditAccount: "detail",
};

const toPayloadRow = (row) => ({
  clientRowId: row.id,
  month: Number(row.month),
  description: (row.description || "").trim(),
  coaAccount: row.coaAccount || null,
  counterparty: row.payee?._id,
  atcCode: row.atcCode,
  rateBasisPoints: rowBasisPoints(row),
  baseCentavos: rowBaseCentavos(row),
  invoiceNo: (row.invoiceNo || "").trim(),
  glMode: row.withholdingOnly ? "withholding_only" : "full",
  creditAccount: row.creditAccount === "ap" ? "ap" : "cash",
});

const FieldError = ({ messages }) =>
  messages?.length ? (
    <p role="alert" className="text-xs text-danger-500 mt-0.5">{messages.join("; ")}</p>
  ) : null;

const inputClass =
  "w-full border rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500";

const EwtRecordsGrid = ({ client, year, defaultMonth, isMicroClient = false, onSaved }) => {
  const [rows, setRows] = useState(() => buildEmptyRows(defaultMonth));
  const [expanded, setExpanded] = useState(() => new Set());
  // { [rowId]: { [cell]: [messages] } } from the last rejected save
  const [rowErrors, setRowErrors] = useState({});
  const [outcomes, setOutcomes] = useState([]);
  // Warnings for rows saved by the last save: [{ id, label, items: [{ code, message }] }]
  const [savedWarnings, setSavedWarnings] = useState([]);
  const [serverSaidMicro, setServerSaidMicro] = useState(false);

  const clientId = client?._id;

  const draft = useEncodingDraft({
    screen: DRAFT_SCREENS.WHT_EWT,
    clientId,
    fiscalYear: year,
    rows,
    isRowEmpty,
  });

  const updateRow = useCallback((id, patch) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...(typeof patch === "function" ? patch(r) : patch) } : r)));
  }, []);

  const toggleExpanded = (id) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // A month picked in the header fills only rows that have none.
  useEffect(() => {
    if (!defaultMonth) return;
    setRows((prev) => prev.map((r) => (r.month ? r : { ...r, month: defaultMonth })));
  }, [defaultMonth]);

  // Clearing the payee field leaves the ATC alone (the row is just incomplete);
  // picking a payee of a different type (WI vs WC) clears an ATC that no
  // longer applies.
  const handlePayee = (id, payee) =>
    updateRow(id, (row) => {
      if (!payee || !row.atc || row.atcPayeeType === payee.profile?.payeeType) return { payee };
      return { payee, atc: null, atcPayeeType: null, atcCode: "", lastAtcCode: null, atcRateBasisPoints: null, rate: "" };
    });

  // Picking a DIFFERENT code refills Rate % from it (any override is
  // discarded); re-picking the SAME code keeps an override. Un-picking (editing
  // the text) leaves the row incomplete but remembers the code and the rate.
  const handleAtc = (id, option) =>
    updateRow(id, option
      ? (row) => {
          const sameCode = option.code === (row.atcCode || row.lastAtcCode);
          return {
            atc: option, atcPayeeType: row.payee?.profile?.payeeType || null, atcCode: option.code, lastAtcCode: option.code,
            atcRateBasisPoints: option.rateBasisPoints,
            rate: sameCode && row.rate !== "" ? row.rate : String(option.rateBasisPoints / 100),
          };
        }
      : (row) => ({ atc: null, atcPayeeType: null, atcCode: "", lastAtcCode: row.atcCode || row.lastAtcCode, atcRateBasisPoints: null }));

  const addRows = () => setRows((prev) => [...prev, ...buildEmptyRows(defaultMonth)]);
  const removeRow = (id) => setRows((prev) => (prev.length <= MIN_ROWS ? prev : prev.filter((r) => r.id !== id)));
  const clearAll = () => {
    setRows(buildEmptyRows(defaultMonth));
    setRowErrors({});
  };

  const handleRestoreDraft = () => {
    const restored = draft.restoreDraft();
    if (restored?.length) setRows(withClientRowIds(restored));
  };

  const filledCount = useMemo(() => rows.filter((r) => !isRowEmpty(r)).length, [rows]);
  const incompleteCount = useMemo(() => rows.filter(isRowIncomplete).length, [rows]);
  const totals = useMemo(
    () => rows.reduce((acc, r) => {
      if (isRowEmpty(r)) return acc;
      const base = rowBaseCentavos(r);
      const tax = rowTaxCentavos(r);
      return { base: acc.base + (Number.isFinite(base) ? base : 0), tax: acc.tax + (tax ?? 0) };
    }, { base: 0, tax: 0 }),
    [rows]
  );

  const handleSave = async () => {
    if (!clientId || !year) {
      notify.error("Couldn't save", "Select a client and a year first.");
      return;
    }

    const { toSave, incomplete } = classifyRows(rows, isRowIncomplete, isRowEmpty);
    setRowErrors({});
    setSavedWarnings([]);

    if (toSave.length === 0) {
      notify.error(
        "Couldn't save",
        incomplete.length
          ? `${incomplete.length} row${incomplete.length !== 1 ? "s are" : " is"} incomplete — each needs a month, a payee, an ATC, a rate and a tax base. Nothing was sent.`
          : "Add at least one row before saving."
      );
      return;
    }

    const payloadRows = toSave.map(toPayloadRow);
    const groupId = `wht-ewt-${year}`;
    const label = `EWT records · ${year}`;
    // Stable key per payload, kept with the draft so a retry after a dropped
    // response replays rather than re-inserts (same as the VAT grid).
    const { key } = draft.keyForGroup(groupId, { year, rows: payloadRows });
    setPendingIdempotencyKey(key);

    let response;
    try {
      response = await withholdingTaxAPI.saveRecords(clientId, { year, rows: payloadRows });
    } catch (error) {
      const hasResponse = Boolean(error?.status);
      if (error?.lineErrors?.length) {
        const byRow = {};
        for (const e of error.lineErrors) {
          const rowId = toSave[(e.line ?? 0) - 1]?.id;
          if (!rowId) continue;
          const cells = (byRow[rowId] ||= {});
          for (const [field, message] of Object.entries(e.fields || {})) {
            const cell = FIELD_CELL[field] || "description";
            (cells[cell] ||= []).push(message);
          }
        }
        setRowErrors(byRow);
      }
      setOutcomes([{
        id: groupId,
        label,
        status: hasResponse ? "failed" : "unknown",
        sentCount: payloadRows.length,
        message: hasResponse
          ? `${error.message || "The server rejected this batch"} — nothing was saved. Fix the highlighted rows and save again.`
          : "No response from the server — these rows may or may not have been saved. Check the records below before saving again.",
      }]);
      notify.error(hasResponse ? "Couldn't save" : "Save unconfirmed", "Your rows are still here.");
      return;
    }

    const saved = response?.data || [];
    const confirmedIds = saved.map((d) => d.clientRowId).filter(Boolean);
    setRows((prev) => rowsAfterSave(prev, confirmedIds, MIN_ROWS, () => buildEmptyRow(defaultMonth)));
    draft.clearConfirmedRows(confirmedIds);
    draft.releaseKey(groupId);

    // Warnings are per sent line (1-based). Micro is client-level: one banner.
    const warnings = response?.warnings || [];
    const perRow = new Map();
    const byRecordId = {};
    let micro = false;
    for (const w of warnings) {
      if (w.code === "MICRO_TAXPAYER") {
        micro = true;
        continue;
      }
      const sent = toSave[(w.line ?? 0) - 1];
      if (!sent) continue;
      if (!perRow.has(sent.id)) {
        perRow.set(sent.id, {
          id: sent.id,
          label: `${sent.payee?.name || "Payee"} · ${sent.atcCode} · ₱${formatCentavos(rowBaseCentavos(sent))}`,
          items: [],
        });
      }
      perRow.get(sent.id).items.push({ code: w.code, message: w.message });
      const record = saved.find((d) => d.clientRowId === sent.id);
      if (record) (byRecordId[record._id] ||= []).push({ code: w.code, message: w.message });
    }
    setSavedWarnings([...perRow.values()]);
    if (micro) setServerSaidMicro(true);

    setOutcomes([{ id: groupId, label, status: "saved", sentCount: payloadRows.length, savedCount: saved.length }]);
    const keptNote = incomplete.length
      ? ` ${incomplete.length} incomplete row${incomplete.length !== 1 ? "s were" : " was"} kept in the grid.`
      : "";
    notify.success("Saved", `${saved.length} withholding record${saved.length !== 1 ? "s" : ""} for ${year}.${keptNote}`);
    onSaved?.(saved, byRecordId);
  };

  const { mutate: runSave, isPending: saving } = useGuardedMutation(handleSave);
  const runSaveSafely = () => {
    runSave()?.catch((error) => {
      console.error("Unexpected error while saving EWT records:", error);
      notify.error("Couldn't save", "Something went wrong before the rows could be sent. Your rows are still here.");
    });
  };

  return (
    <div>
      <RestoreDraftModal
        draft={draft.pendingDraft}
        onRestore={handleRestoreDraft}
        onDiscard={draft.discardDraft}
        screenLabel="EWT records"
      />

      {(isMicroClient || serverSaidMicro) && <MicroTaxpayerBanner />}

      {outcomes.length > 0 && (
        <div className="mb-4">
          <SaveOutcomePanel outcomes={outcomes} onDismiss={() => setOutcomes([])} />
        </div>
      )}

      {savedWarnings.length > 0 && (
        <div role="status" aria-label="Saved with warnings" className="mb-4 border border-warning-200 bg-warning-50 rounded-lg p-3">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-warning-700 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" /> Saved with warnings — please review
            </p>
            <Button variant="ghost" size="sm" onClick={() => setSavedWarnings([])}>Dismiss</Button>
          </div>
          <ul className="mt-2 space-y-1">
            {savedWarnings.map((w) => (
              <li key={w.id} data-testid="saved-warning" className="text-sm text-warning-700">
                <span className="font-medium">{w.label}:</span> {w.items.map((i) => i.message).join(" · ")}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="overflow-x-auto border border-ink-line rounded-lg">
        <table className="min-w-full divide-y divide-ink-line">
          <thead className="bg-ink-canvas">
            <tr>
              <th className="w-10 px-2 py-3"><span className="sr-only">Details</span></th>
              <th className="px-3 py-3 text-left text-xs font-medium text-ink-body uppercase tracking-wider w-[8%]">Month</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-ink-body uppercase tracking-wider w-[22%]">Description / COA</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-ink-body uppercase tracking-wider w-[18%]">Payee</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-ink-body uppercase tracking-wider w-[18%]">ATC</th>
              <th className="px-3 py-3 text-right text-xs font-medium text-ink-body uppercase tracking-wider w-[12%]">Tax base</th>
              <th className="px-3 py-3 text-right text-xs font-medium text-ink-body uppercase tracking-wider w-[8%]">Rate %</th>
              <th className="px-3 py-3 text-right text-xs font-medium text-ink-body uppercase tracking-wider w-[10%]">Tax withheld</th>
              <th className="px-3 py-3 text-center text-xs font-medium text-ink-body uppercase tracking-wider w-[4%]">Action</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-ink-line">
            {rows.flatMap((row, index) => {
              const errors = rowErrors[row.id] || {};
              const invalid = isRowIncomplete(row) || Object.keys(errors).length > 0;
              const isExpanded = expanded.has(row.id);
              const tax = rowTaxCentavos(row);
              const rateBp = rowBasisPoints(row);
              const rateDiffers = row.atcRateBasisPoints != null && Number.isInteger(rateBp) && rateBp !== row.atcRateBasisPoints;
              const n = index + 1;

              const main = (
                <tr key={row.id} data-row-id={row.id} data-invalid={invalid ? "true" : undefined} className="hover:bg-ink-canvas/60">
                  <td className={`px-2 py-2 text-center align-top ${invalid ? "border-l-2 border-danger-400" : ""}`}>
                    <button
                      type="button"
                      onClick={() => toggleExpanded(row.id)}
                      className="text-ink-body hover:text-ink"
                      aria-label={isExpanded ? `Collapse row ${n}` : `Expand row ${n}`}
                      aria-expanded={isExpanded}
                    >
                      {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    </button>
                  </td>
                  <td className="px-3 py-2 align-top">
                    <select
                      aria-label={`Month row ${n}`}
                      value={row.month || ""}
                      onChange={(e) => updateRow(row.id, { month: Number(e.target.value) || "" })}
                      className={`${inputClass} ${errors.month ? "border-danger-500" : "border-ink-line"}`}
                    >
                      <option value="" disabled>Select</option>
                      {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                    </select>
                    <FieldError messages={errors.month} />
                  </td>
                  <td className="px-3 py-2 align-top">
                    <ChartOfAccountsSearch
                      value={row.coaName || row.description}
                      onChange={(value) => updateRow(row.id, (r) => ({
                        description: value,
                        ...(value && !r.coaName ? { coaAccount: null } : {}),
                      }))}
                      onSelect={(account) => updateRow(row.id, account
                        ? { coaAccount: account._id, coaName: `${account.accountNumber} - ${account.accountName}`, description: account.accountName }
                        : { coaAccount: null, coaName: "", description: "" })}
                      placeholder="Search or enter description"
                      showLinkedBadge={false}
                    />
                    <FieldError messages={errors.description} />
                  </td>
                  <td className="px-3 py-2 align-top">
                    <PayeePicker
                      clientId={clientId}
                      value={row.payee}
                      onChange={(p) => handlePayee(row.id, p)}
                      inputId={`payee-${row.id}`}
                      ariaLabel={`Payee row ${n}`}
                      invalid={Boolean(errors.payee)}
                    />
                    <FieldError messages={errors.payee} />
                  </td>
                  <td className="px-3 py-2 align-top">
                    <AtcPicker
                      clientId={clientId}
                      payeeType={row.payee?.profile?.payeeType}
                      year={year}
                      month={row.month}
                      value={row.atc}
                      onChange={(option) => handleAtc(row.id, option)}
                      inputId={`atc-${row.id}`}
                      ariaLabel={`ATC row ${n}`}
                      invalid={Boolean(errors.atc)}
                    />
                    <FieldError messages={errors.atc} />
                  </td>
                  <td className="px-3 py-2 align-top">
                    <div className="relative">
                      <span className="absolute inset-y-0 left-0 pl-2 flex items-center text-ink-body text-xs">₱</span>
                      <MoneyInput
                        id={`base-${row.id}`}
                        value={row.base}
                        onValueChange={(value) => updateRow(row.id, { base: value })}
                        className={`pl-6 w-full ${errors.base ? "border-danger-500" : ""}`}
                      />
                    </div>
                    <FieldError messages={errors.base} />
                  </td>
                  <td className="px-3 py-2 align-top">
                    <input
                      type="text"
                      inputMode="decimal"
                      aria-label={`Rate % row ${n}`}
                      value={row.rate}
                      onChange={(e) => updateRow(row.id, { rate: e.target.value.replace(/[^\d.]/g, "") })}
                      className={`${inputClass} text-right tabular-nums ${errors.rate ? "border-danger-500" : "border-ink-line"}`}
                    />
                    {rateDiffers && (
                      <p className="text-xs text-warning-700 mt-0.5">ATC rate {(row.atcRateBasisPoints / 100).toFixed(2)}%</p>
                    )}
                    <FieldError messages={errors.rate} />
                  </td>
                  <td className="px-3 py-2 align-top">
                    <output
                      aria-label={`Tax withheld row ${n}`}
                      className="block w-full border border-ink-line rounded px-2 py-1.5 text-sm text-right tabular-nums bg-ink-canvas text-ink-body"
                    >
                      {tax === null ? "—" : formatCentavos(tax)}
                    </output>
                  </td>
                  <td className="px-2 py-2 text-center align-top">
                    <button
                      type="button"
                      onClick={() => removeRow(row.id)}
                      disabled={rows.length <= MIN_ROWS}
                      title={rows.length <= MIN_ROWS ? `Minimum ${MIN_ROWS} rows` : "Remove row"}
                      aria-label={`Remove row ${n}`}
                      className="text-danger-500 hover:text-danger-700 disabled:text-ink-line"
                    >
                      <Trash2 className="w-4 h-4 mx-auto" />
                    </button>
                  </td>
                </tr>
              );

              if (!isExpanded) return [main];

              const profile = row.payee?.profile;
              const detail = (
                <tr key={`${row.id}-detail`}>
                  <td colSpan={9} className="bg-ink-canvas px-6 py-4">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div>
                        <div className="text-xs font-medium text-ink-body mb-1">Payee TIN</div>
                        <div className="text-sm text-ink tabular-nums" data-testid={`tin-${row.id}`}>
                          {profile ? (profile.tin9 ? `${profile.tin9}-${profile.tinBranch}` : <span className="text-warning-700">No TIN on file</span>) : "—"}
                        </div>
                      </div>
                      <div>
                        <label htmlFor={`invoice-${row.id}`} className="block text-xs font-medium text-ink-body mb-1">Invoice / OR no.</label>
                        <input
                          id={`invoice-${row.id}`}
                          type="text"
                          value={row.invoiceNo}
                          onChange={(e) => updateRow(row.id, { invoiceNo: e.target.value })}
                          className={`${inputClass} border-ink-line bg-white`}
                        />
                      </div>
                      <label className="flex items-start gap-2 text-sm text-ink cursor-pointer">
                        <input
                          type="checkbox"
                          checked={row.withholdingOnly}
                          onChange={(e) => updateRow(row.id, { withholdingOnly: e.target.checked })}
                          className="h-4 w-4 mt-0.5 accent-primary-500"
                        />
                        <span>
                          Already in the books? (withholding only)
                          <span className="block text-xs text-ink-body">The expense is booked elsewhere, e.g. via VAT input.</span>
                        </span>
                      </label>
                      <div>
                        <label htmlFor={`credit-${row.id}`} className="block text-xs font-medium text-ink-body mb-1">Credit to</label>
                        <select
                          id={`credit-${row.id}`}
                          value={row.creditAccount}
                          onChange={(e) => updateRow(row.id, { creditAccount: e.target.value })}
                          className={`${inputClass} border-ink-line bg-white`}
                        >
                          <option value="cash">Cash</option>
                          <option value="ap">Accounts payable</option>
                        </select>
                      </div>
                    </div>
                    <FieldError messages={errors.detail} />
                  </td>
                </tr>
              );
              return [main, detail];
            })}
          </tbody>
          <tfoot className="bg-ink-canvas border-t-2 border-ink-line">
            <tr>
              <td className="px-2 py-3"></td>
              <td colSpan={4} className="px-3 py-3 text-sm font-medium text-ink-body">
                Totals ({filledCount} filled, {rows.length - filledCount} empty)
                {incompleteCount > 0 && <span className="ml-2 text-danger-500">{incompleteCount} incomplete</span>}
              </td>
              <td className="px-3 py-3 text-sm font-medium text-ink text-right tabular-nums">₱{formatCentavos(totals.base)}</td>
              <td className="px-3 py-3"></td>
              <td className="px-3 py-3 text-sm font-medium text-ink text-right tabular-nums">₱{formatCentavos(totals.tax)}</td>
              <td className="px-2 py-3"></td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div className="mt-4 flex justify-between items-center">
        <Button variant="outline" size="sm" icon={Plus} onClick={addRows}>Add 5 more rows</Button>
        <div className="flex items-center gap-3">
          <Button variant="dangerOutline" onClick={clearAll}>Clear all</Button>
          <DraftStatusIndicator lastSavedAt={draft.lastSavedAt} isSaving={draft.isSaving} />
          <div className="text-right">
            <Button onClick={runSaveSafely} loading={saving} disabled={saving || !clientId || !year}>
              {saving ? "Saving…" : `Save ${filledCount} record${filledCount !== 1 ? "s" : ""} (${year || "—"})`}
            </Button>
            <p className="text-xs text-ink-body mt-1">Empty rows are skipped; incomplete rows stay in the grid</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EwtRecordsGrid;
